import { HttpClient, HttpBackend } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { environment } from '../../environments/environment';
import moment from "moment";
import * as emasync from '../modules/emasync';
import { VideoServerService, VideoServerUser } from './videoserver.service';
import { ICategoryAndReasonTranslated } from 'app/models/alarm-reasons.model';
import { ApiService } from './api.service';
import { IconsService } from './icons.service';
import { IconResponse } from 'app/models/icons.model';

//
// Active alert states (not including completed)
//
export enum CrAlertState {
  Available,
  Reserved,
  Started
}

//
// What an alert says about the alarm route's redirect targets. The alert
// server resolves redirectGroups into alertsrv.resolvedRedirectUsers: the
// members of those groups that are on duty and have been heard from in the
// last ~10 hours. That list is informational - it does not decide routing -
// and it carries no presence: the members in it may well be offline.
//
export enum CrRedirectRoute {
  // the alert does not say - an alarm that predates group resolution, or an
  // alert server that does not report redirect targets at all
  Unknown,
  // the route names no targets at all --> sending reaches nobody, and that is
  // a configuration fault rather than an empty shift
  NoTargets,
  // redirectRecipients is who the route reaches. Empty means the groups hold
  // nobody on duty: everyone has logged out or been out of contact for hours
  Resolved
}

export type CameraVisitUrl = {
  id: string,
  videoUrl: string
}

//
// CrAlert is the representation of an alert, when viewed and processed
// in the control room application. It has read-only projection of the
// original alert parameters (from wire) and some read-write parameters
// for storing alert related data in application.
//
export class CrAlert {
  constructor(
    // read-only projection of alert parameters
    readonly userId: string,
    readonly alertId: string,
    readonly time: moment.Moment,
    readonly header: string,
    readonly productId: string,
    readonly body: string,
    readonly reservedBy: string,
    readonly startedBy: string,
    readonly reRoutedBy: string,
    readonly state: CrAlertState,
    readonly isForwarded: boolean,
    readonly address: string,
    readonly deviceType: string,
    readonly alertGroup: number,
    readonly alertNode: number,
    readonly customerId: string,
    readonly customerName: string,
    readonly baseMISDN: string,
    readonly place: string,
    readonly increment: number,
    readonly isCameraVisit: boolean,
    readonly cameraVisitURL: string,
    readonly cameraVisitURLArray: CameraVisitUrl[],
    readonly cameraVisitStartTime: moment.Moment,
    readonly cameraVisitDurationMinutes: number,
    readonly alarmType: string,
    // users that would receive the alert if it was sent to the app: the
    // redirect targets of the alarm route, with groups resolved to members
    readonly redirectRecipients: string[],
    // what that list is worth --> see CrRedirectRoute
    readonly redirectRoute: CrRedirectRoute,
    // read-write parameters for use in application
    public iconResponse: IconResponse,
    public allowTransition: boolean,
    public transitional: boolean,
    public notes: CrNote[],
    public noteCache: string,
    public alarmReasons: ICategoryAndReasonTranslated[],
    public locationName: string,
    public positionLink: string,
    public positionLatitude: number,
    public positionLongitude: number,
    public positionPrecision: number,
    public positionType: string,
    public showChevron: boolean,
    public styleOutOfTime: boolean,
    public styleBadlyOutOfTime: boolean,
    public showTime: number,
  ) {}
};


//
// Different alert update types
//
export enum CrAlertEventType {
  UpdateAllAlerts,
  UpdateOneAlert,
  DeleteOneAlert
};


//
// Alert update is notified to application as CrAlertEvent
// via alerts observables (active and forwarded)
//
export class CrAlertEvent {
  constructor(
    readonly userId: string,
    readonly type: CrAlertEventType,
    readonly alertList: CrAlert[],
    readonly alertId: string | null
  ) {}
}


//
// Internal state of crsync service
//
export enum CrSyncState {
  Init,
  Connecting,
  Reconnecting,
  Online,
  Offline,
  Error,
  Completed
};


//
// Team member information
//
export class CrTeamMember {
  constructor(
    readonly userId: string,
    readonly name: string,
    readonly initials: string,
    readonly isOnline: boolean,
    // multiple phone numbers are supported for multi-SIM phones
    readonly phoneNumbers: CrPhoneNumber[],
    readonly photoAvatar: string,
    // when the alert server last saw this member, present or not. Undefined
    // when the alert server does not report it.
    readonly lastSeen: string | undefined = undefined
  ) {}

  public toString = () : string => this.name;
};


//
// Phone number is a pair of display name (in practice, mobile
// operator name) and a phone number
//
export class CrPhoneNumber {
  constructor(
    readonly displayName: string,
    readonly phoneNumber: string
  ) {}
};


//
// Team member event provides up-to-date list of team members
// and their statuses (for the main or alias user indicated by userId).
//
// Team member array is alphabetically sorted by name.
//
export class CrTeamMemberEvent {
  constructor(
    readonly userId: string,
    readonly teamMembers: CrTeamMember[]
  ) {}
};

// Notes added by operator or nurse
export class CrNote {
  constructor(
    public text: string,
    public writtenBy: string,
    public time: any
  ){}
}

//
// CrSyncService serves the following purposes:
// - adapts pure JS/TS emasync module to Angular application as a service
// - converts evolving, loose wire events (from callback) to rigid, strongly
//   typed events (RxJS Observable), and back
// - hides alert server details (e.g. REST API) from control room application
// - manages and multiplexes several user alias connections
//
@Injectable()
export class CrSyncService {
  private http: HttpClient;
  private authData: any;
  private sessions: { [userId: string]: EmaSyncSession } = {};
  private _state: CrSyncState = CrSyncState.Init;
  private _userId: string | null = null;
  private _aliases: any[] = [];

  private alertsObservable: Observable<CrAlertEvent> | null = null;
  private alertsSubscriber: Subscriber<CrAlertEvent> | null = null;
  private teamObservable: Observable<CrTeamMemberEvent> | null = null;
  private teamSubscriber: Subscriber<CrTeamMemberEvent> | null = null;
  private fwdAlertsObservable: Observable<CrAlertEvent> | null = null;
  private fwdAlertsSubscriber: Subscriber<CrAlertEvent> | null = null;
  private onlineObservable: Observable<boolean> | null = null;
  private onlineSubscriber: Subscriber<boolean> | null = null;
  private exitObservable: Observable<void> | null = null;
  private exitSubscriber: Subscriber<void> | null = null;


  // wire (monitored) alerts contain all alerts as received from alert server
  // (these are transformed to EmaAlert lists for the application)
  private _wireAlerts: { [alertId: string]: WireAlert } = {};
  private _wireMonitoredAlerts: { [alertId: string]: WireAlert } = {};
  private readonly _iconLookupConcurrency = 8;
  private _activeIconLookups = 0;
  private _iconLookupQueue: Array<() => Promise<void>> = [];

  private asBaseUrl = environment.alertServerBaseUrl;

  // watches the sessions' push channels for outages emasync does not report,
  // often enough that a brief one cannot pass between two checks unseen
  private static readonly PUSH_CHANNEL_WATCH_MS = 1000;
  private pushChannelWatch: NodeJS.Timeout | null = null;


  //
  // public methods
  //

  constructor(
    backend: HttpBackend,
    private videoServerService: VideoServerService,
    private api: ApiService,
    private iconsService: IconsService,
    private zone: NgZone,
  ) {
    // use pristine backend, because default one will override
    // authentication headers in an interceptor
    this.http = new HttpClient(backend);
  }


  //
  // Connect to alert server and possibly video controller.
  //
  // When username and password are provided, basic authentication with these credentials are used.
  // Otherwise connection is established with authentication token stored in local storage.
  // Connection is periodically retried until it succeeds.
  //
  // Returns false if username nad password is not given and authtoken is not found, or connection
  // has already been initiated. Otherwise true is returned.
  //
  connect(username: string = "", password: string = "", reconnect: boolean = false): boolean {
    if (!reconnect && this._state !== CrSyncState.Init && this._state !== CrSyncState.Completed) return false;
    let authToken: string  = "";
    if (!username || !password) {
      const userStr = localStorage.getItem("alertServerUser");
      if (userStr) {
        let user: any = null;
        try {
          user = JSON.parse(userStr);
          authToken = user.authToken;
          username = user.username;
          console.log("Connecting to Alert Server with authtoken, username:", username);
        } catch(e) {
          // intentionally ignored: proceed without auth token
        }
      }
      if (!authToken) {
        console.warn("Could not retrieve authtoken for alertserver.")
        return false;
      }
    } else {
      console.log("Connecting to Alert Server using basic authentication");
    }

    const origUsername = username;
    const origPassword = password;

    this._state = reconnect ? CrSyncState.Reconnecting : CrSyncState.Connecting;
    const url = this.asBaseUrl + '/api/login' + (authToken ? "?authtoken=" + authToken : "");
    const data = {};
    const options = authToken ? undefined : {
      headers: { 'Authorization': "Basic " + btoa(`${username}:${password}`) }
    };
    this.http.post(url, data, options).subscribe((res: any) => {
      if (res.userId && res.companyId && res.authToken) {
        console.log("Alert Server login OK.")
        this.authData = res;
        this._userId = res.userId;
        if (!this.authData.username) this.authData.username = username;
        this._aliases = Array.isArray(res.aliases) ? res.aliases : [];

        localStorage.setItem("alertServerUser", JSON.stringify(res));
        this._connectEmaSync();
        const customerIds: string[] = [];

        const aliasUsers = this._aliases.map(alias => {

          // Collect customer IDs for alias features
          customerIds.push(alias.companyId);

          return VideoServerUser.fromObject(alias);
        }
        ).filter(user => !!user);

        this.videoServerService.trackVisits(aliasUsers);

        // Get customer alias features
        this.api.getCustomerAliasFeatures(customerIds).subscribe((res) => {
          localStorage.setItem("aliasFeatures", JSON.stringify(res));
        });

      } else {
        console.warn("Error: unable to authenticate. Retrying in 5 seconds...");
        this._evaluateOnlineState(true);
        setTimeout(() => this.connect(origUsername, origPassword, true), 5*1000);
      }
    }, _err => {
      console.warn("Error: unable to connect. Retrying in 5 seconds...");
      this._evaluateOnlineState(true);
      setTimeout(() => this.connect(origUsername, origPassword, true), 5*1000);
    });

    this.alertsObservable = new Observable(subscriber => {
      this.alertsSubscriber = subscriber;
    });
    this.teamObservable = new Observable(subscriber => {
      this.teamSubscriber = subscriber;
    });
    this.fwdAlertsObservable = new Observable(subscriber => {
      this.fwdAlertsSubscriber = subscriber;
    });
    this.onlineObservable = new Observable(subscriber => {
      this.onlineSubscriber = subscriber;
    });
    this.exitObservable = new Observable(subscriber => {
      this.exitSubscriber = subscriber;
    });

    return true;
  }

  //
  // Disconnect terminates all active sessions to alert server and video controller.
  //
  disconnect(): void {
    this._state = CrSyncState.Completed;
    this._userId = null;
    if (this.pushChannelWatch) {
      clearInterval(this.pushChannelWatch);
      this.pushChannelWatch = null;
    }
    Object.keys(this.sessions).forEach((userId: string) => {
      this.sessions[userId]?.emaSync?.stop();
    });
    this.videoServerService.untrackVisits();
    this.sessions = {};
    if (this.alertsSubscriber) this.alertsSubscriber.complete();
    this.alertsSubscriber = null;
    this.alertsObservable = null;
    if (this.teamSubscriber) this.teamSubscriber.complete();
    this.teamSubscriber = null;
    this.teamObservable = null;
    if (this.fwdAlertsSubscriber) this.fwdAlertsSubscriber.complete();
    this.fwdAlertsSubscriber = null;
    this.fwdAlertsObservable = null;
    if (this.onlineSubscriber) this.onlineSubscriber.complete();
    this.onlineSubscriber = null;
    this.onlineObservable = null;
    if (this.exitSubscriber) this.exitSubscriber.complete();
    this.exitSubscriber = null;
    this.exitObservable = null;
  }

  //
  // Observable for receiving active alert events
  //
  get activeAlerts(): Observable<CrAlertEvent> {
    return this.alertsObservable as Observable<CrAlertEvent>;
  }

  //
  // Observable for receiving up-to-date team meber list
  //
  get teamMembers(): Observable<CrTeamMemberEvent> {
    return this.teamObservable as Observable<CrTeamMemberEvent>;
  }

  //
  // Observable for receiving forwarded alert events
  //
  get forwardedAlerts(): Observable<CrAlertEvent> {
    return this.fwdAlertsObservable as Observable<CrAlertEvent>;
  }

  //
  // Observable for receiving online status events
  //
  get isOnline(): Observable<boolean> {
    return this.onlineObservable as Observable<boolean>;
  }

  //
  // Observable for receiving exit events
  //
  get shouldExit(): Observable<void> {
    return this.exitObservable as Observable<void>;
  }

  //
  // Internal state of the service
  //
  get state(): CrSyncState {
    return this._state;
  }

  //
  // User ID of the main user
  //
  get userId(): string | null {
    return this._userId;
  }

  //
  // List of user aliases. It is expected (but not validated) that
  // each entry should have userId property.
  //
  get aliases(): any[] {
    return this._aliases;
  }

  //
  // Return team member data, if synchronised. Note that the returned data
  // may later be updated as new state is synchronised from server.
  //
  getTeamMember(memberId: string, sessionUserId: string | null = null): CrTeamMember | undefined {
    let session: EmaSyncSession | null = null;
    if (sessionUserId) {
      session = this.sessions[sessionUserId];
      if (!session) return undefined;
      return session.teamMembers.find(entry => entry.userId === memberId);
    }

    // no session user id given --> search all sessions
    let member: CrTeamMember | undefined = undefined;
    Object.keys(this.sessions).some(sessionUserId => {
      member = this.sessions[sessionUserId]?.teamMembers?.find(entry => entry.userId === memberId);
      return !!member;
    });
    return member;
  }

  //
  // Team members of a session, as far as they have been synchronised.
  //
  getTeamMembers(sessionUserId: string): CrTeamMember[] {
    return this.sessions[sessionUserId]?.teamMembers || [];
  }

  //
  // True when the alert server reports team presence for all EMA app users.
  // Older alert servers only see users that hold a connection to them, which
  // misses every app user that reports presence to the user presence service
  // instead, and servers running without presence handling see none at all.
  // Both advertise no flag --> their presence cannot be interpreted.
  //
  get appUserPresenceComplete(): boolean {
    return this.authData?.appUserPresenceComplete === true;
  }

  //
  // True when the team member state of a session can be relied on: the alert
  // server reports app user presence, the session exists and is online, and
  // its last team sync succeeded. Anything else (old server, no session,
  // reconnecting, a sync in flight or failed, a push channel that has been
  // away) means the state is unknown rather than up to date. A team that
  // synchronised and came back empty is known, not unknown: that customer has
  // no app users, so no alarm sent to the app would reach anyone.
  //
  isTeamStateKnown(sessionUserId: string): boolean {
    const session: EmaSyncSession | undefined = this.sessions[sessionUserId];
    if (!session) return false;

    this._notePushChannelState(session);

    return this.appUserPresenceComplete &&
      session.state === CrSyncState.Online &&
      session.teamSynchronised;
  }

  //
  // A socket that drops and recovers within emasync's offline delay emits no
  // event at all, so nothing tells us that presence pushes were missed. The
  // channel's own state does show the gap, but only while it lasts --> it is
  // watched rather than waited for, and a session that was away keeps its team
  // state unknown until a sync answers for it.
  //
  private _notePushChannelState(session: EmaSyncSession): void {
    if (session.emaSync?.state !== emasync.EmaSyncState.ONLINE) {
      if (!session.pushChannelDown) {
        session.pushChannelDown = true;
        // a team request in flight was asked before the outage --> its answer
        // is not an answer about the team we have now
        session.presenceEpoch++;
      }
      session.teamSynchronised = false;
    } else {
      session.pushChannelDown = false;
    }
  }

  private _watchPushChannels(): void {
    let changed = false;
    Object.keys(this.sessions).forEach((userId: string) => {
      const session = this.sessions[userId];
      if (!session) return;
      const wasKnown = session.teamSynchronised;
      this._notePushChannelState(session);
      changed = changed || wasKnown !== session.teamSynchronised;
    });
    // the watch runs outside Angular, so a channel going away is the one change
    // to availability that no event follows --> ask for a pass when it happens,
    // and only then
    if (changed) this.zone.run(() => {});
  }

  //
  // List of team members that are currently online.
  // Note: the requesting user is removed from the list.
  //
  getOnlineTeamMembers(sessionUserId: string): CrTeamMember[] {
    if (sessionUserId) {
      const session: EmaSyncSession | undefined = this.sessions[sessionUserId];
      if (!session) return [];
      return session.teamMembers.filter(entry => entry.isOnline === true && entry.userId !== sessionUserId);
    }
    return [];
  }


  //
  // Request alert server to change alert's state to a new active state.
  //
  changeAlertState(userId: string, alertId: string, state: CrAlertState): boolean {
    // make shallow(ish) copy for provisional state change
    const alert = { ...this._wireAlerts[alertId]?.alert };
    alert.alertsrv = { ...this._wireAlerts[alertId]?.alert?.alertsrv };
    // set new state into copy
    switch(state) {
      case CrAlertState.Available:
        alert.alertsrv.state = "AVAILABLE";
        break;
      case CrAlertState.Reserved:
        alert.alertsrv.state = "RESERVED";
        break;
      case CrAlertState.Started:
        alert.alertsrv.state = "STARTED";
        break;
    }
    this._putAlert(userId, alert);
    return true;
  }

  //
  // Request alert server to change alert's state to completed.
  //
  completeAlert(userId: string, alertId: string): void {
    // make shallow(ish) copy for provisional state change
    const alert = { ...this._wireAlerts[alertId]?.alert };
    alert.alertsrv = { ...this._wireAlerts[alertId]?.alert?.alertsrv };
    // set completed state into copy
    alert.alertsrv.state = "COMPLETED";

    this._putAlert(userId, alert);
  }

  //
  // Request alert server to change alert's state to forwarded. Alert
  // server should delete the current alert and spawn a new alert
  // (with same alert ID) targeting forwardees (if Aspa is properly
  // configured).
  //
  forwardAlert(userId: string, alertId: string): void {
    this._putAlertRedirect(userId, alertId);
  }


  //
  // Request alert server to add a note to an alert
  //
  addNote(userId: string, alertId: string, noteText: string): void {
    this._postAlertNote(userId, alertId, noteText);
  }

  //
  // Request alert server to reset alert incrementer (counting additional
  // alerts from same device) back to zero.
  //
  resetAlertIncrement(userId: string, alertId: string): void {
    this._deleteAlertIncrementer(userId, alertId);
  }

  //
  // Log when a phone call is initiated to an Origon GSM number for a specific visit.
  //
  setPhoneCallStarted(origonGsmNumber: string, visitId: string): void {
    this._postPhoneCallStarted(origonGsmNumber, visitId);
  }
  
  //
  // private methods
  //

  private _sync(esConfig: emasync.EmaSyncConfiguration): void {
    console.log(`Syncing: ${esConfig.userId}@${esConfig.companyId}`);

    const session = new EmaSyncSession(esConfig);
    this.sessions[esConfig.userId] = session;
    session.emaSync.start(esConfig, async event => {
      // we are online (unless this is offline event, which is explicitly handled later)
      session.state = CrSyncState.Online;
      if (event.type === emasync.EmaSyncEventType.SYNC_ONLINE) {
        // already set to online --> do nothing
      } else if (event.type === emasync.EmaSyncEventType.SYNC_CONFIRMED) {
        // ping succeeded --> online (which is already set) --> do nothing
      } else if (event.type === emasync.EmaSyncEventType.SYNC_OFFLINE) {
        session.state = CrSyncState.Offline;
        // presence pushes stopped while the session was away, so the team
        // snapshot is no older than the outage but no fresher either. Going
        // online again triggers a team sync, which sets this back.
        session.teamSynchronised = false;
        session.presenceEpoch++;
      } else if (event.type === emasync.EmaSyncEventType.SYNC_TERMINATED) {
        // send exit event
        if (this.exitSubscriber) this.exitSubscriber.next();
      } else if (event.type === emasync.EmaSyncEventType.ALERT_UPDATE_ALL) {
        if (event.data && Array.isArray(event.data)) {
          const knownSessionAlertIds = new Set<string>();
          const previousSessionIcons: { [alertId: string]: IconResponse } = {};
          Object.keys(this._wireAlerts).forEach(id => {
            if (this._wireAlerts[id]?.userId === esConfig.userId) {
              knownSessionAlertIds.add(id);
              const iconResponse = this._wireAlerts[id]?.alert?.iconResponse;
              if (iconResponse) {
                previousSessionIcons[id] = iconResponse;
              }
            }
          });

          // update complete alerts associative array
          const otherSessionAlerts: { [alertId: string]: WireAlert } = {};
          Object.keys(this._wireAlerts).forEach(id => {
            if (this._wireAlerts[id]?.userId !== esConfig.userId) otherSessionAlerts[id] = this._wireAlerts[id];
          });
          this._wireAlerts = otherSessionAlerts;
          event.data.forEach(entry => {
            if (this._isValidAlert(entry)) {
              const previousIcon = previousSessionIcons[entry.alarmID];
              if (!entry.iconResponse && previousIcon) {
                entry.iconResponse = previousIcon;
              }
              this._wireAlerts[entry.alarmID] = new WireAlert(esConfig.userId, entry);
            }
          });
          // transform all wire alerts to a list of EmaAlerts, sort by alert time and push update
          const emaAlerts: CrAlert[] = Object.keys(this._wireAlerts).map(id =>
            this._makeEmaAlertFromWire(this._wireAlerts[id]?.userId, this._wireAlerts[id]?.alert)
          );
          this._enrichNewAlertsWithIcons(emaAlerts, knownSessionAlertIds, this._wireAlerts, (alertWithIcon: CrAlert) => {
            this._pushAlerts(esConfig.userId, CrAlertEventType.UpdateOneAlert, [alertWithIcon], null);
          });
          emaAlerts.sort((a: CrAlert, b: CrAlert) => a.time.diff(b.time));
          this._pushAlerts(esConfig.userId, CrAlertEventType.UpdateAllAlerts, emaAlerts, null);
          this._enrichAlertsWithRedirectTargets(esConfig.userId);
        }
      } else if (event.type === emasync.EmaSyncEventType.ALERT_UPDATE_ONE) {
        if (event.data) {
          // upsert new entry
          if (this._isValidAlert(event.data, false)) {
            if (event.data.alertsrv.state === "COMPLETED") {
              // remove completed alert
              delete this._wireAlerts[event.data.alarmID];
              this._pushAlerts(esConfig.userId, CrAlertEventType.DeleteOneAlert, null, event.data.alarmID);
            } else {
              // update active alert
              const hadAlert = this._wireAlerts[event.data.alarmID] !== undefined;
              const previousIcon = this._wireAlerts[event.data.alarmID]?.alert?.iconResponse;
              const isNewAlert = !hadAlert;
              this._wireAlerts[event.data.alarmID] = new WireAlert(esConfig.userId, event.data);
              if (!this._wireAlerts[event.data.alarmID]?.alert?.iconResponse && previousIcon) {
                this._wireAlerts[event.data.alarmID].alert.iconResponse = previousIcon;
              }
              const emaAlert = this._makeEmaAlertFromWire(esConfig.userId, event.data);
              if (isNewAlert) {
                await this._ensureIconResponse(emaAlert);
                this._wireAlerts[event.data.alarmID].alert.iconResponse = emaAlert.iconResponse;
              }
              this._pushAlerts(esConfig.userId, CrAlertEventType.UpdateOneAlert, [emaAlert], null);
            }
          }
        }
      } else if (event.type === emasync.EmaSyncEventType.ALERT_DELETE_ONE) {
        if (event.data) {
          // delete alert
          delete this._wireAlerts[event.data];
          this._pushAlerts(esConfig.userId, CrAlertEventType.DeleteOneAlert, null, event.data);
        }
      } else if (event.type === emasync.EmaSyncEventType.ALERT_MONITOR_UPDATE_ALL) {
        if (event.data && Array.isArray(event.data)) {
          const knownMonitoredAlertIds = new Set<string>();
          const previousMonitoredIcons: { [alertId: string]: IconResponse } = {};
          Object.keys(this._wireMonitoredAlerts).forEach(id => {
            if (this._wireMonitoredAlerts[id]?.userId === esConfig.userId) {
              knownMonitoredAlertIds.add(id);
              const iconResponse = this._wireMonitoredAlerts[id]?.alert?.iconResponse;
              if (iconResponse) {
                previousMonitoredIcons[id] = iconResponse;
              }
            }
          });

          // update complete alerts associative array
          const otherSessionAlerts: { [alertId: string]: WireAlert } = {};
          Object.keys(this._wireMonitoredAlerts).forEach(id => {
            if (this._wireMonitoredAlerts[id]?.userId !== esConfig.userId) otherSessionAlerts[id] = this._wireMonitoredAlerts[id];
          });
          this._wireMonitoredAlerts = otherSessionAlerts;
          event.data.forEach(entry => {
            if (this._isValidAlert(entry)) {
              const previousIcon = previousMonitoredIcons[entry.alarmID];
              if (!entry.iconResponse && previousIcon) {
                entry.iconResponse = previousIcon;
              }
              this._wireMonitoredAlerts[entry.alarmID] = new WireAlert(esConfig.userId, entry);
            }
          });
          const emaAlerts: CrAlert[] = Object.keys(this._wireMonitoredAlerts).map(id =>
            this._makeEmaAlertFromWire(this._wireMonitoredAlerts[id]?.userId, this._wireMonitoredAlerts[id]?.alert, true)
          );
          this._enrichNewAlertsWithIcons(emaAlerts, knownMonitoredAlertIds, this._wireMonitoredAlerts, (alertWithIcon: CrAlert) => {
            this._pushFwdAlerts(esConfig.userId, CrAlertEventType.UpdateOneAlert, [alertWithIcon], null);
          });
          emaAlerts.sort((a: CrAlert, b: CrAlert) => a.time.diff(b.time));
          this._pushFwdAlerts(esConfig.userId, CrAlertEventType.UpdateAllAlerts, emaAlerts, null);
        }
      } else if (event.type === emasync.EmaSyncEventType.ALERT_MONITOR_UPDATE_ONE) {
        if (event.data) {
          // upsert new entry
          if (this._isValidAlert(event.data, false)) {
            if (event.data.alertsrv.state === "COMPLETED") {
              // remove completed alert
              delete this._wireMonitoredAlerts[event.data.alarmID];
              this._pushFwdAlerts(esConfig.userId, CrAlertEventType.DeleteOneAlert, null, event.data.alarmID);
            } else {
              // update active alert
              const hadAlert = this._wireMonitoredAlerts[event.data.alarmID] !== undefined;
              const previousIcon = this._wireMonitoredAlerts[event.data.alarmID]?.alert?.iconResponse;
              const isNewAlert = !hadAlert;
              this._wireMonitoredAlerts[event.data.alarmID] = new WireAlert(esConfig.userId, event.data);
              if (!this._wireMonitoredAlerts[event.data.alarmID]?.alert?.iconResponse && previousIcon) {
                this._wireMonitoredAlerts[event.data.alarmID].alert.iconResponse = previousIcon;
              }
              const emaAlert = this._makeEmaAlertFromWire(esConfig.userId, event.data, true);
              if (isNewAlert) {
                await this._ensureIconResponse(emaAlert);
                this._wireMonitoredAlerts[event.data.alarmID].alert.iconResponse = emaAlert.iconResponse;
              }
              this._pushFwdAlerts(esConfig.userId, CrAlertEventType.UpdateOneAlert, [emaAlert], null);
            }
          }
        }
      } else if (event.type === emasync.EmaSyncEventType.ALERT_MONITOR_DELETE_ONE) {
        if (event.data) {
          // delete entry
          delete this._wireMonitoredAlerts[event.data];
          this._pushFwdAlerts(esConfig.userId, CrAlertEventType.DeleteOneAlert, null, event.data);
        }
      } else if (event.type === emasync.EmaSyncEventType.TEAM_SYNC_NEEDED ||
                  event.type === emasync.EmaSyncEventType.TEAM_MEMBER_ONLINE_STATE_SYNC ||
                  event.type === emasync.EmaSyncEventType.TEAM_MEMBER_PROPERTY_SYNC) {
        this._handleMemberEvent(esConfig.userId, session, event);
      }

      //
      this._evaluateOnlineState();
    });
  }

  private _evaluateOnlineState(reconnecting: boolean = false): void {
    // check online state of all sessions; service state is online only when all sessions are online

    // do not evaluate in initial/terminal states
    const noEvalStates = [CrSyncState.Error, CrSyncState.Init, CrSyncState.Completed];
    if (noEvalStates.lastIndexOf(this._state) !== -1) return;

    // handle reconnecting special case
    if (reconnecting)  {
      if (this.state === CrSyncState.Connecting) {
        // first reconnect --> set offline
        if (this.onlineSubscriber) this.onlineSubscriber.next(false);
      }
      return;
    }

    // evaluate online state of service
    const previousState = this._state;
    const isOnline = Object.keys(this.sessions).every((userId: string) =>
        this.sessions[userId]?.state === CrSyncState.Online || this.sessions[userId]?.state === CrSyncState.Connecting);
    this._state = isOnline ? CrSyncState.Online : CrSyncState.Offline;

    // send event on changes
    if (previousState !== CrSyncState.Online && this._state === CrSyncState.Online) {
      // became online --> send online event
      if (this.onlineSubscriber) this.onlineSubscriber.next(true);
    } else if (previousState !== CrSyncState.Offline && this._state === CrSyncState.Offline) {
      // became offline --> send online event
      if (this.onlineSubscriber) this.onlineSubscriber.next(false);
    }
  }

  private _connectEmaSync(): void {
    // outside Angular: this only writes a flag that is read on the next change
    // detection pass either way, and a second-by-second tick of the whole
    // control room would be a high price for watching a socket
    if (!this.pushChannelWatch) {
      this.zone.runOutsideAngular(() => {
        this.pushChannelWatch = setInterval(
          () => this._watchPushChannels(), CrSyncService.PUSH_CHANNEL_WATCH_MS
        );
      });
    }

    // connect with emasync
    const esConfig = {
      url: this.asBaseUrl,
      userId: this.authData.userId,
      companyId: this.authData.companyId,
      authToken: this.authData.authToken,
      info: {},
      reloginFn: () => console.log("Relogin not implemented"),
      pingPeriodSec: 60,
      pingPeriodTimeoutSec: 10,
      getAlertsMaxDelaySec: 15,
      offlineDelaySec: 10,
      alwaysGetMonitoredAlerts: true,
      verbose: false
    };
    this._sync(esConfig);

    if (this._aliases) {
      // make alias connections, too
      this._aliases.forEach(alias => {
        if (!alias.userId || !alias.companyId || !alias.authToken) return;
        const aliasConfig = {...esConfig};
        aliasConfig.userId = alias.userId;
        aliasConfig.companyId = alias.companyId;
        aliasConfig.authToken = alias.authToken;
        this._sync(aliasConfig);
      });

    }
  }

  private _isValidAlert(alert: any, filterCompleted: boolean = true): boolean {
    return (alert.alarmID &&
            alert.companyID &&
            alert.alertsrv.state &&
            (!filterCompleted || alert.alertsrv.state !== "COMPLETED"));
  }

  private _pushAlerts(userId: string, type: CrAlertEventType, alerts: CrAlert[] | null, alertId: string | null): void {
    if (!alerts) alerts = [];
    if (this.alertsSubscriber) this.alertsSubscriber.next(new CrAlertEvent(userId, type, alerts, alertId));
  }

  private _pushFwdAlerts(userId: string, type: CrAlertEventType, alerts: CrAlert[] | null, alertId: string | null): void {
    if (!alerts) alerts = [];
    if (this.fwdAlertsSubscriber) this.fwdAlertsSubscriber.next(new CrAlertEvent(userId, type, alerts, alertId));
  }

  private async _ensureIconResponse(alert: CrAlert): Promise<void> {
    if (!alert || alert.iconResponse || !alert.alertId) return;
    alert.iconResponse = await this.iconsService.findIcon(alert);
  }

  private _scheduleIconLookup(task: () => Promise<void>): void {
    this._iconLookupQueue.push(task);
    this._drainIconLookupQueue();
  }

  private _drainIconLookupQueue(): void {
    while (this._activeIconLookups < this._iconLookupConcurrency && this._iconLookupQueue.length > 0) {
      const task = this._iconLookupQueue.shift();
      if (!task) continue;

      this._activeIconLookups += 1;
      task()
        .catch(error => console.warn("Icon enrichment failed:", error))
        .finally(() => {
          this._activeIconLookups -= 1;
          this._drainIconLookupQueue();
        });
    }
  }

  private _enrichNewAlertsWithIcons(
    alerts: CrAlert[],
    knownAlertIds: Set<string>,
    targetWireAlerts: { [alertId: string]: WireAlert },
    onIconResolved?: (alert: CrAlert) => void
  ): void {
    const newAlerts = alerts.filter(alert => !knownAlertIds.has(alert.alertId));
    newAlerts.forEach(alert => {
      this._scheduleIconLookup(async () => {
        await this._ensureIconResponse(alert);
        const wireAlert = targetWireAlerts[alert.alertId]?.alert;
        if (wireAlert) {
          wireAlert.iconResponse = alert.iconResponse;
        }
        if (alert.iconResponse && onIconResolved) {
          onIconResolved(alert);
        }
      });
    });
  }

  private _makeEmaAlertFromWire(userId: string, wireAlert: any, monitoredAlert: boolean = false): CrAlert {
    const _addressFromAlert = (alert: any): string => {
      if (alert.postAddress && alert.postCode && alert.postOffice) {
        return alert.postAddress + ", " + alert.postCode + " " + alert.postOffice
      }
      return "";
    };

    // AsPa reports the alarm route's redirect targets on VERIFI alerts as
    // redirectUsers (individual users, locations, companies) and redirectGroups,
    // the latter resolved to members by the alert server. An empty result means
    // the route has no fallback --> sending to the app reaches nobody.
    //
    // A location or company target is not a user id and so matches no team
    // member: it reads as a recipient of unknown presence and the alert stays
    // sendable, which is the safe answer but means a route targeting a whole
    // location is never checked. Resolving those belongs with the alert server,
    // next to the group resolution it already does.
    const _redirectRecipientsFromAlert = (alert: any): { recipients: string[], route: CrRedirectRoute } => {
      const users = Array.isArray(alert.redirectUsers) ? alert.redirectUsers : null;
      const groups = Array.isArray(alert.redirectGroups) ? alert.redirectGroups : null;
      const resolved = Array.isArray(alert.alertsrv?.resolvedRedirectUsers)
        ? alert.alertsrv.resolvedRedirectUsers
        : null;
      // a user targeted directly can also be a member of a redirect group, so
      // the two lists overlap
      const recipients = [...new Set<string>([...(users || []), ...(resolved || [])])];

      // groups the alert server has not resolved leave members we cannot see
      if (groups?.length && !resolved) return { recipients, route: CrRedirectRoute.Unknown };
      // nothing reported at all
      if (!users && !groups && !resolved) return { recipients, route: CrRedirectRoute.Unknown };
      // the route itself names nobody
      if (!users?.length && !groups?.length) return { recipients, route: CrRedirectRoute.NoTargets };

      return { recipients, route: CrRedirectRoute.Resolved };
    };

    const _redirect = _redirectRecipientsFromAlert(wireAlert);

   const  _stateFromAlert = (alert: any): CrAlertState => {
      if (alert.alertsrv.state === "STARTED") return CrAlertState.Started;
      if (alert.alertsrv.state === "RESERVED") return CrAlertState.Reserved;
      return CrAlertState.Available;
    };
    return new CrAlert(
      userId,
      wireAlert.alarmID,
      wireAlert.alertRoutingType === 'Verifi' ? moment(wireAlert.alertsrv.receivedTime) : moment(wireAlert.alarmTime),
      wireAlert.deviceName,
      wireAlert.deviceID,
      wireAlert.info || "",
      wireAlert.alertsrv.reservedBy,
      wireAlert.alertsrv.startedBy,
      wireAlert.reRoutedBy,
      _stateFromAlert(wireAlert),
      monitoredAlert,
      _addressFromAlert(wireAlert),
      wireAlert.devicePrefixClass,
      wireAlert.alarmGroup,
      wireAlert.alarmNode,
      wireAlert.companyID,
      wireAlert.companyName || "",
      wireAlert.baseMISDN,
      wireAlert.place,
      wireAlert.alertsrv.alertIncrement,
      wireAlert.alertsrv.isCameraVisit,
      wireAlert.alertsrv.cameraVisitURL,
      Array.isArray(wireAlert.alertsrv.cameraVisitURLArray) ? wireAlert.alertsrv.cameraVisitURLArray : [],
      wireAlert.alertsrv.cameraVisitStartTime,
      wireAlert.alertsrv.cameraVisitDurationMinutes,
      wireAlert.alarmType,
      _redirect.recipients,
      _redirect.route,
      wireAlert.iconResponse,
      !monitoredAlert,
      false,
      wireAlert.alertsrv.notes,
      wireAlert.alertsrv.noteCache,
      wireAlert.alarmReasons,
      wireAlert.locationName,
      wireAlert.positionLink,
      wireAlert.positionLatitude,
      wireAlert.positionLongitude,
      wireAlert.positionPrecision,
      wireAlert.positionType,
      wireAlert.showChevron,
      wireAlert.styleOutOfTime,
      wireAlert.styleBadlyOutOfTime,
      wireAlert.showTime
    );
  }

  private _handleMemberEvent(userId: string, session: EmaSyncSession, event: emasync.EmaSyncEvent) {
    const __handleNextEvent = () => {
      // this is a (not tail-call optimised) recursive function
      // --> all paths should drop handled event from buffer before recursing
      //     i.e. session.teamSyncEventBuffer.shift()

      if (session.teamSyncEventBuffer.length === 0) {
        // all events handled --> send update to observers
        if (this.teamSubscriber) this.teamSubscriber.next(new CrTeamMemberEvent(userId, session.teamMembers));
        return;
      }

      // handle next event from buffer
      const event = session.teamSyncEventBuffer[0];
      if (event.type === emasync.EmaSyncEventType.TEAM_SYNC_NEEDED) {
        // a request still in flight when the session went offline describes the
        // team as it was before the outage --> its answer is not a sync
        const epoch = session.presenceEpoch;
        // a sync is asked for when the session reconnects or the server says the
        // team changed, so from here until the answer arrives the snapshot is
        // not something to decide on. These are rare enough that the card
        // showing nothing meanwhile beats it showing something stale.
        session.teamSynchronised = false;
        const _syncFailed = () => {
          session.teamSynchronised = false;
          session.teamSyncEventBuffer.shift();
          __handleNextEvent();
        };
        this._getTeamMembers(userId).subscribe(
          (data: any) => {
            // anything but an array of members leaves the team unknown, and the
            // buffered event has to be dropped either way or the queue stalls
            if (!Array.isArray(data) || epoch !== session.presenceEpoch) {
              _syncFailed();
            } else {
              // mutate existing objects by replacing existing, adding new ones and then removing deleted
              data.forEach(member => {
                let found = false;
                session.teamMembers.some((existingMember: CrTeamMember, ix: number) => {
                  found = (member._id === existingMember.userId);
                  if (found) session.teamMembers[ix] = this._makeMemberFromWire(member);
                  return found;
                });
                if (!found) session.teamMembers.push(this._makeMemberFromWire(member));
              });
              // filter rather than splice while iterating: removing in place
              // shifts the array and skips the member after each departure
              session.teamMembers = session.teamMembers.filter(
                (m: CrTeamMember) => data.some(newMember => newMember._id === m.userId)
              );
              session.teamMembers.sort();
              session.teamSynchronised = true;
              session.teamSyncEventBuffer.shift();
              __handleNextEvent();
            }
          },
          (_error: any) => _syncFailed()
        );
      } else if (event.type === emasync.EmaSyncEventType.TEAM_MEMBER_ONLINE_STATE_SYNC) {
        // a push we cannot use is still a push we have handled: leaving it at
        // the head of the buffer stalls every later team event for this
        // session, presence included, until the next login builds a new one
        if (event.data && typeof event.data.userId === "string" && event.data.userId !== userId && typeof event.data.connected === "boolean") {
          session.teamMembers = session.teamMembers.map(member => {
            if (member.userId === event.data.userId) {
              // the alert server's lastSeen only arrives with a full team sync,
              // so a member going offline here would keep the time of an earlier
              // session. We saw them online until this push, which the server
              // debounces by a few seconds --> stamp it as the time last seen.
              // only the moment a member goes offline is a time we witnessed:
              // a repeated offline push would otherwise keep moving the time
              // forward for someone who left hours ago
              const wentOffline = member.isOnline && !event.data.connected;
              const lastSeen = wentOffline ? new Date().toISOString() : member.lastSeen;
              return new CrTeamMember(member.userId, member.name, member.initials, event.data.connected, member.phoneNumbers, member.photoAvatar, lastSeen);
            } else {
              return member;
            }
          });
        }
        session.teamSyncEventBuffer.shift();
        __handleNextEvent();
      } else if (event.type === emasync.EmaSyncEventType.TEAM_MEMBER_PROPERTY_SYNC) {
        // the push identifies its subject in the user object; the userId beside
        // it is not always there
        const pushedUser = event.data?.user;
        const describesMember = !!pushedUser && typeof pushedUser._id === "string";

        if (describesMember && pushedUser._id !== userId) {
          session.teamMembers = session.teamMembers.map(member => {
            if (member.userId === pushedUser._id) {
              const newMember = this._makeMemberFromWire(pushedUser);
              // the push carries the whole user, and the alert server reports
              // presence in it --> take it when it is there, and keep what we
              // have when the push is only about names or phone numbers
              const reportsPresence = typeof pushedUser.alertsrv?.present === "boolean";
              const isOnline = reportsPresence ? newMember.isOnline : member.isOnline;
              // never lose a time we already have to a push that omits it
              return new CrTeamMember(member.userId, newMember.name, newMember.initials, isOnline, newMember.phoneNumbers, newMember.photoAvatar, newMember.lastSeen || member.lastSeen);
            } else {
              return member;
            }
          });
        } else if (!describesMember) {
          // the push says team data changed without saying what - the alert
          // server sends it with just a timestamp when membership moves - so
          // ask for the team rather than dropping the news
          const syncQueued = session.teamSyncEventBuffer.some(
            (queued: emasync.EmaSyncEvent) => queued.type === emasync.EmaSyncEventType.TEAM_SYNC_NEEDED
          );
          if (!syncQueued) {
            session.teamSyncEventBuffer.push(
              new emasync.EmaSyncEvent(emasync.EmaSyncEventType.TEAM_SYNC_NEEDED)
            );
          }
        }
        session.teamSyncEventBuffer.shift();
        __handleNextEvent();
      } else {
        // this branch should not happen
        console.warn("Not handled:", userId, event);
        session.teamSyncEventBuffer.shift();
        __handleNextEvent();
      }
    }

    // first buffer event for handling
    session.teamSyncEventBuffer.push(event);
    if (session.teamSyncEventBuffer.length > 1) {
      // earlier event is being handled --> current event will be handled later
      return;
    }
    // handle recursively immediate event and events buffered during handling
    __handleNextEvent();
  }

  private _makeMemberFromWire(wireMember: any): CrTeamMember {
    if (!wireMember || !wireMember._id) return;

    let name = "N/A";
    let initials = "";
    if (wireMember.firstname && wireMember.lastname) {
      name = wireMember.lastname + ", " + wireMember.firstname
      initials = wireMember.firstname.charAt(0) + wireMember.lastname.charAt(0);
    } else if (wireMember.firstname) {
      name = wireMember.firstname;
      initials = wireMember.firstname.charAt(0);
    } else if (wireMember.lastname) {
      name = wireMember.lastname;
      initials = wireMember.lastname.charAt(0);
    }
    const phoneNumbers = [];
    if (wireMember.alertsrv && wireMember.alertsrv.simCards && Array.isArray(wireMember.alertsrv.simCards)) {
      wireMember.alertsrv.simCards.forEach(tel => {
        const displayName = tel.displayName || "N/A";
        if (tel.phoneNumber) phoneNumbers.push(new CrPhoneNumber(displayName, tel.phoneNumber));
      });
    }
    let isOnline = false;
    if (wireMember.alertsrv && typeof wireMember.alertsrv.present === "boolean") {
      isOnline = wireMember.alertsrv.present;
    }
    let photoAvatar: string = undefined;
    if (wireMember.alertsrv && typeof wireMember.alertsrv.photoAvatar === "string") {
      photoAvatar = wireMember.alertsrv.photoAvatar;
    }
    let lastSeen: string = undefined;
    if (wireMember.alertsrv && typeof wireMember.alertsrv.lastSeen === "string") {
      lastSeen = wireMember.alertsrv.lastSeen;
    }

    return new CrTeamMember(
      wireMember._id,
      name,
      initials,
      isOnline,
      phoneNumbers,
      photoAvatar,
      lastSeen
    );
  }

  private _getUsernameWithId(userId: string): string {
    if (userId === this._userId) {
      // main user
      return this.authData.username || userId;
    }

    let username = "";
    this._aliases.some(alias => {
      const found = (userId === alias.userId);
      if (found) {
        username = alias.username;
      }
      return found;
    });
    return username || userId;
  }

  private _putAlert(userId: string, alert: any): void {
    const session = this.sessions[userId];
    if (!session) return;

    const url = this.asBaseUrl + '/api/alerts/' + alert.alarmID;
    const data = alert;
    const options = { headers: session.sessionHeaders };
    this.http.put(url, data, options).subscribe();
  }

  private _putAlertRedirect(userId: string, alertId: string): void {
    const session = this.sessions[userId];
    if (!session) return;
    const url = this.asBaseUrl + '/api/alerts/' + alertId + '/redirect';
    const data = {};
    const options = { headers: session.sessionHeaders };
    this.http.put(url, data, options).subscribe();
  }

  //
  // The alert list emasync refetches on every connect comes without the alarm
  // route's redirect targets: emasync asks for /api/alerts plainly, and the
  // alert server only puts them in behind redirectTargets=1. The socket
  // payloads carry them, so without this a reload leaves every alert with no
  // recipients until it happens to be pushed again.
  //
  private _getAlertsWithRedirectTargets(userId: string): Observable<any> | undefined {
    const session = this.sessions[userId];
    if (!session) return undefined;
    const url = this.asBaseUrl + '/api/alerts?redirectTargets=1';
    const options = { headers: session.sessionHeaders };
    return this.http.get(url, options);
  }

  //
  // Fills the redirect targets in for alerts that arrived without them, and
  // pushes the ones that changed. Only fills what is missing: an alert the
  // socket has already described is fresher than anything this asks for.
  //
  private _enrichAlertsWithRedirectTargets(userId: string): void {
    const request = this._getAlertsWithRedirectTargets(userId);
    if (!request) return;

    request.subscribe(
      (data: any) => {
        if (!Array.isArray(data)) return;
        data.forEach(entry => {
          const wireAlert = this._wireAlerts[entry?.alarmID];
          if (!wireAlert || wireAlert.userId !== userId) return;
          const alert = wireAlert.alert;
          const hasTargets = Array.isArray(alert.redirectUsers) ||
            Array.isArray(alert.redirectGroups) ||
            Array.isArray(alert.alertsrv?.resolvedRedirectUsers);
          if (hasTargets) return;

          alert.redirectUsers = entry.redirectUsers;
          alert.redirectGroups = entry.redirectGroups;
          if (alert.alertsrv && entry.alertsrv) {
            alert.alertsrv.resolvedRedirectUsers = entry.alertsrv.resolvedRedirectUsers;
          }
          this._pushAlerts(userId, CrAlertEventType.UpdateOneAlert,
            [this._makeEmaAlertFromWire(userId, alert)], null);
        });
      },
      (_error: any) => console.warn("Could not read redirect targets for", userId)
    );
  }

  private _getTeamMembers(userId: string): Observable<any>  {
    const session = this.sessions[userId];
    if (!session) return;
    const url = this.asBaseUrl + '/api/users';
    const options = { headers: session.sessionHeaders };
    return this.http.get(url, options);
  }

  private _postAlertNote(userId: string, alertId: string, noteText: string): void  {
    const session = this.sessions[userId];
    if (!session) return;

    const url = this.asBaseUrl + '/api/alerts/' + alertId + '/notes';
    const data = {
      writtenBy: this._getUsernameWithId(session.config.userId),
      time: new Date().toISOString(),
      entryMethod: "WRITTEN",
      text: noteText
    };
    const options = { headers: session.sessionHeaders };
    this.http.post(url, data, options).subscribe();
  }

  private _deleteAlertIncrementer(userId: string, alertId: string): void  {
    const session = this.sessions[userId];
    if (!session) return;

    const url = this.asBaseUrl + '/api/alerts/' + alertId + '/incrementer';
    const options = { headers: session.sessionHeaders };
    this.http.delete(url, options).subscribe();
  }

  private _postPhoneCallStarted(origonGsmNumber: string, visitId: string): void {
    const session = this.sessions[this.userId];
    if (!session) return;

    const url = this.asBaseUrl + '/api/logs/origon-calls';
    const options = { headers: session.sessionHeaders };
    this.http.post(url, { origonGsmNumber, visitId }, options).subscribe();
  }
}

//
// LOCAL CLASSES
//



//
// Session that is established with alert server
// using emasync module.
//
// There are typically multiple simultaneous sessions:
// one for main user account and one for each user alias.
//
class EmaSyncSession {
  constructor(config: emasync.EmaSyncConfiguration) {
    this.config = config;
    this.emaSync = new emasync.EmaSync();
    this.state = CrSyncState.Connecting;
    this.sessionHeaders = {
      "x-alertsrv-authtoken": config.authToken,
      "x-alertsrv-sessionid": config.authToken,
      "x-everon-companyid": config.companyId
    }
    this.teamMembers = [];
    this.teamSyncEventBuffer = [];
    this.teamSynchronised = false;
    this.presenceEpoch = 0;
    this.pushChannelDown = false;
  }
  config: emasync.EmaSyncConfiguration;
  emaSync: emasync.EmaSync;
  state: CrSyncState;
  sessionHeaders: any;
  teamMembers: CrTeamMember[];
  teamSyncEventBuffer: emasync.EmaSyncEvent[];
  // false until a team sync has succeeded, and again after one has failed
  // --> team member state (including online state) cannot be relied on
  teamSynchronised: boolean;
  // incremented whenever the session loses its push channel, so that answers
  // to requests made before the outage can be told from current ones
  presenceEpoch: number;
  // whether the last look at the push channel found it down, so that an outage
  // counts once however often it is observed
  pushChannelDown: boolean;
}


//
// Pair of user ID and alert that is used for caching
// wire alerts for passing complete alerts back to
// alert server REST API
//
class WireAlert {
  constructor(
    public userId: string,
    public alert: any
  ) {}
}
